import React, { useState } from "react";
import axios from "axios";
import { UserPlus, Check, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const RSVPForm = ({ slug, themeColor }) => {
  const [form, setForm] = useState({ name: "", email: "", attending: "yes", plus_ones: 0, note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/public/events/${slug}/rsvp`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        attending: form.attending === "yes",
        plus_ones: Number(form.plus_ones) || 0,
        note: form.note.trim() || null,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit your RSVP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="rsvp-thanks" className="bg-white rounded-2xl soft-shadow p-6 border-2 border-emerald-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <Check className="w-5 h-5" />
          </div>
          <div>
            <div className="font-serif italic text-navy-700 text-xl">Thanks, {form.name.split(" ")[0]}!</div>
            <p className="text-sm text-navy-700/60">Your RSVP has been recorded. See you soon.</p>
          </div>
        </div>
      </div>
    );
  }

  const accent = themeColor || "#e76f51";

  return (
    <form onSubmit={submit} data-testid="rsvp-form" className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <UserPlus className="w-5 h-5" style={{ color: accent }} />
        <h3 className="font-serif italic text-navy-700 text-2xl">RSVP</h3>
      </div>
      <p className="text-sm text-navy-700/60 -mt-2">Let the hosts know you&apos;re coming.</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Your name</label>
          <input
            required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="rsvp-name"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none text-navy-700"
            style={{ borderColor: form.name ? accent : undefined }}
          />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Email <span className="normal-case text-navy-700/40 font-normal">(optional)</span></label>
          <input
            type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="rsvp-email"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none text-navy-700"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Attending?</label>
          <select
            value={form.attending} onChange={(e) => setForm({ ...form, attending: e.target.value })}
            data-testid="rsvp-attending"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none text-navy-700 bg-white"
          >
            <option value="yes">Yes, I&apos;ll be there</option>
            <option value="no">Sorry, can&apos;t make it</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Bringing +?</label>
          <input
            type="number" min="0" max="20" value={form.plus_ones}
            onChange={(e) => setForm({ ...form, plus_ones: e.target.value })}
            data-testid="rsvp-plus-ones"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none text-navy-700"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Note to the hosts (optional)</label>
        <textarea
          rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          data-testid="rsvp-note"
          className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none text-navy-700"
          maxLength={500}
        />
      </div>
      {err && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
      <button
        disabled={busy || !form.name.trim()} type="submit"
        data-testid="rsvp-submit"
        style={{ backgroundColor: accent }}
        className="w-full text-white py-2.5 rounded-full font-semibold hover:brightness-95 disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send RSVP"}
      </button>
    </form>
  );
};

export default RSVPForm;
